import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@common/apis/axios-base-query';
import { IAgent } from '../interfaces/IAgent';
import { IAgentVersion } from '../interfaces/IAgentVersion';

const agentsPath = () => `/v1/agents`;
const adminAgentsPath = () => `/portal/agents`;
const adminVersionsPath = (agentId: string) => `${adminAgentsPath()}/${agentId}/versions`;

interface CreateAgentPayload {
  slug: string;
  name: string;
  description?: string;
}

interface CreateVersionPayload {
  agentId: string;
  extraction_prompt: string;
  exploration_hint?: string;
  validation_prompt?: string;
  output_schema?: Record<string, unknown>;
  adaptive_card_template?: Record<string, unknown> | null;
}

interface UpdateVersionPayload {
  agentId: string;
  versionId: string;
  extraction_prompt?: string;
  exploration_hint?: string;
  validation_prompt?: string;
  output_schema?: Record<string, unknown>;
  adaptive_card_template?: Record<string, unknown> | null;
}

interface PublishVersionPayload {
  agentId: string;
  versionId: string;
}

interface UpdateAgentPayload {
  agentId: string;
  name?: string;
  description?: string;
}

interface UpdateAgentOrgConfigPayload {
  agentId: string;
  enabled: boolean;
}

interface DeleteAgentPayload {
  agentId: string;
}

interface DeleteVersionPayload {
  agentId: string;
  versionId: string;
}

interface ListVersionsResponse {
  values?: IAgentVersion[];
}

interface ListAgentsResponse {
  values?: IAgent[];
}

export interface IAgentDetail extends IAgent {
  version?: IAgentVersion;
}

export const AgentsApi = createApi({
  reducerPath: 'agentsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Agents', 'AgentVersions'],
  endpoints: (builder) => ({
    getAgent: builder.query<IAgentDetail, string>({
      query: (agentId) => ({
        pathname: `/v1/agents/${encodeURIComponent(agentId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, agentId) => [{ type: 'Agents', id: agentId }],
    }),

    getAgents: builder.query<IAgent[], void>({
      query: () => ({
        pathname: '/v1/agents',
        method: 'GET',
        params: { include_disabled: true },
      }),
      transformResponse: (response: ListAgentsResponse | IAgent[]) =>
        Array.isArray(response) ? response : (response.values ?? []),
      providesTags: ['Agents'],
    }),

    createAgent: builder.mutation<IAgent, CreateAgentPayload>({
      query: ({ slug, name, description }) => ({
        pathname: agentsPath(),
        method: 'POST',
        data: { slug, name, description, category: 'custom', agent_kind: 'extraction' },
      }),
      invalidatesTags: ['Agents'],
    }),

    getAgentVersions: builder.query<IAgentVersion[], { agentId: string }>({
      query: ({ agentId }) => ({
        pathname: adminVersionsPath(agentId),
        method: 'GET',
      }),
      transformResponse: (response: ListVersionsResponse | IAgentVersion[]) =>
        Array.isArray(response) ? response : (response.values ?? []),
      providesTags: (_result, _error, { agentId }) => [{ type: 'AgentVersions', id: agentId }],
    }),

    createVersion: builder.mutation<IAgentVersion, CreateVersionPayload>({
      query: ({ agentId, ...body }) => ({
        pathname: adminVersionsPath(agentId),
        method: 'POST',
        data: { output_format: 'json', ...body },
      }),
      invalidatesTags: (_result, _error, { agentId }) => [
        { type: 'AgentVersions', id: agentId },
        'Agents',
      ],
    }),

    updateVersion: builder.mutation<IAgentVersion, UpdateVersionPayload>({
      query: ({ agentId, versionId, ...body }) => ({
        pathname: `${adminVersionsPath(agentId)}/${versionId}`,
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_result, _error, { agentId }) => [
        { type: 'AgentVersions', id: agentId },
        'Agents',
      ],
    }),

    publishVersion: builder.mutation<IAgentVersion, PublishVersionPayload>({
      query: ({ agentId, versionId }) => ({
        pathname: `${adminVersionsPath(agentId)}/${versionId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { agentId }) => [
        { type: 'AgentVersions', id: agentId },
        'Agents',
      ],
    }),

    updateAgent: builder.mutation<IAgent, UpdateAgentPayload>({
      query: ({ agentId, ...body }) => ({
        pathname: `${adminAgentsPath()}/${agentId}`,
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_result, _error, { agentId }) => [
        { type: 'Agents', id: agentId },
        'Agents',
      ],
    }),

    deleteAgent: builder.mutation<void, DeleteAgentPayload>({
      query: ({ agentId }) => ({
        pathname: `${adminAgentsPath()}/${agentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agents'],
    }),

    deleteVersion: builder.mutation<void, DeleteVersionPayload>({
      query: ({ agentId, versionId }) => ({
        pathname: `${adminVersionsPath(agentId)}/${versionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { agentId }) => [{ type: 'AgentVersions', id: agentId }],
    }),

    updateAgentOrgConfig: builder.mutation<void, UpdateAgentOrgConfigPayload>({
      query: ({ agentId, enabled }) => ({
        pathname: `${adminAgentsPath()}/${agentId}/org-config`,
        method: 'PUT',
        data: { enabled },
      }),
      invalidatesTags: ['Agents'],
    }),
  }),
});

export const {
  useGetAgentQuery,
  useGetAgentsQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useGetAgentVersionsQuery,
  useCreateVersionMutation,
  useUpdateVersionMutation,
  usePublishVersionMutation,
  useDeleteAgentMutation,
  useDeleteVersionMutation,
  useUpdateAgentOrgConfigMutation,
} = AgentsApi;
